package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.requests.ObservePendingRequestsUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn

/**
 * SH-164: how many requests wait for the signed-in manager's decision at the current workplace
 * (pending absences and swaps awaiting a manager), for the Requests tab's badge (or More's, where
 * Requests lives for owners and financial managers) and the owner dashboard's inbox card. Zero for
 * those who do not decide requests there. App-wide, like [NotificationsViewModel].
 */
@OptIn(ExperimentalCoroutinesApi::class)
class PendingRequestsViewModel(
    getCurrentUserUseCase: GetCurrentUserUseCase,
    sessionManager: WorkplaceSessionManager,
    private val observePendingRequests: ObservePendingRequestsUseCase
) : ViewModel() {

    val count: StateFlow<Int> = combine(getCurrentUserUseCase(), sessionManager.currentWorkplaceId) { user, workplaceId ->
        workplaceId?.takeIf { user != null && Capability.APPROVE_REQUESTS in user.capabilitiesAt(it) }
    }
        // Profile updates of the same person at the same workplace keep the listeners.
        .distinctUntilChanged()
        .flatMapLatest { workplaceId ->
            if (workplaceId == null) flowOf(0) else observePendingRequests(workplaceId).map { it.count }
        }
        .distinctUntilChanged()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), 0)
}
